import {
  quickBooksBootstrapSchema,
  quickBooksOAuthStartResponseSchema,
  quickBooksQueueSyncResponseSchema,
  quickBooksRedriveResponseSchema,
  quickBooksSyncResponseSchema,
  quickBooksSyncRecordsResponseSchema,
  quickBooksV2HealthSchema,
} from "@crewfsm-extension/quickbooks-contracts";

export function createQuickBooksApi(input: {
  getSessionToken: () => string;
  refreshSessionToken: () => Promise<string>;
}) {
  let refreshPromise: Promise<string> | null = null;

  const request = async (
    path: string,
    init: RequestInit = {},
    retry = true,
  ): Promise<unknown> => {
    const response = await fetch(`/runtime${path}`, {
      ...init,
      headers: {
        ...init.headers,
        authorization: `Bearer ${input.getSessionToken()}`,
      },
    });
    if (response.status === 401 && retry) {
      refreshPromise ??= input.refreshSessionToken().finally(() => {
        refreshPromise = null;
      });
      await refreshPromise;
      return request(path, init, false);
    }
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;
      throw new Error(
        payload?.error ?? `QuickBooks request failed (${response.status}).`,
      );
    }
    return response.status === 204 ? null : response.json();
  };

  return {
    async bootstrap() {
      return quickBooksBootstrapSchema.parse(await request("/v1/bootstrap"));
    },
    async startOAuth() {
      return quickBooksOAuthStartResponseSchema.parse(
        await request("/v1/oauth/start", { method: "POST" }),
      );
    },
    async sync() {
      return quickBooksSyncResponseSchema.parse(
        await request("/v1/sync", { method: "POST" }),
      );
    },
    async disconnect() {
      await request("/v1/connection", { method: "DELETE" });
    },
    async v2Health() {
      return quickBooksV2HealthSchema.parse(await request("/v2/health"));
    },
    async queueV2Sync(reconcile = false) {
      return quickBooksQueueSyncResponseSchema.parse(
        await request("/v2/sync", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            idempotencyKey: crypto.randomUUID(),
            reconcile,
          }),
        }),
      );
    },
    async v2Records(filters: { entityType?: string; status?: string } = {}) {
      const query = new URLSearchParams({ limit: "100" });
      if (filters.entityType) query.set("entityType", filters.entityType);
      if (filters.status) query.set("status", filters.status);
      return quickBooksSyncRecordsResponseSchema.parse(
        await request(`/v2/records?${query}`),
      );
    },
    async redriveV2Job(jobId: string) {
      return quickBooksRedriveResponseSchema.parse(
        await request(`/v2/jobs/${encodeURIComponent(jobId)}/redrive`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            idempotencyKey: crypto.randomUUID(),
            reconcile: false,
          }),
        }),
      );
    },
  };
}
